import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { ProductosService } from '../../services/productos.service';
import { ToastrService } from 'ngx-toastr';
import { debounceTime, distinctUntilChanged, switchMap, catchError, of } from 'rxjs';

interface Producto {
  id: number;
  codigo: string;
  nombre: string;
  precio: string | number;
  stock: number;
  peso: number | null;
  unidadMedida: string;
  descripcion?: string;
  ingredientes?: string[];
  beneficios?: string;
  modoUso?: string;
  condicionesAlmacenamiento?: string;
  etiquetas?: string[];
  contenido?: string;
  [key: string]: any; // Para campos adicionales
}

@Component({
  selector: 'app-search-dialog',
  templateUrl: './search-dialog.component.html',
  styleUrls: ['./search-dialog.component.scss']
})
export class SearchDialogComponent implements OnInit {
  busquedaForm: FormGroup;
  productosEncontrados: Producto[] = [];
  mostrarSugerencias: boolean = false;
  buscando: boolean = false;
  productoSeleccionado: Producto | null = null;
  mostrarDetalles: boolean = false;

  constructor(
    private fb: FormBuilder,
    private productosService: ProductosService,
    private toastr: ToastrService,
    public dialogRef: MatDialogRef<SearchDialogComponent>
  ) {
    this.busquedaForm = this.fb.group({
      busqueda: ['', [Validators.required, Validators.minLength(2)]]
    });
  }

  ngOnInit(): void {
    this.configurarBusquedaReactiva();
  }

  private configurarBusquedaReactiva(): void {
    this.busquedaForm.get('busqueda')?.valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(termino => {
        this.mostrarSugerencias = termino.length >= 2;
        this.buscando = this.mostrarSugerencias;

        return termino.length >= 2
          ? this.productosService.buscarProductos(termino).pipe(
              catchError(() => {
                this.toastr.error('Error al buscar productos');
                return of([]);
              })
            )
          : of([]);
      })
    ).subscribe({
      next: (productos) => {
        setTimeout(() => {
          this.productosEncontrados = productos;
          this.buscando = false;
        });
      }
    });
  }

  manejarTeclaBusqueda(event: any): void {
    if (event.key === 'Enter') {
      const termino = this.busquedaForm.get('busqueda')?.value;
      if (termino && termino.length >= 2) {
        this.buscando = true;
        this.productosService.buscarProductos(termino).subscribe({
          next: (productos) => {
            this.productosEncontrados = productos;
            this.buscando = false;
            this.mostrarSugerencias = true;
          },
          error: () => {
            this.toastr.error('Error al buscar productos');
            this.buscando = false;
          }
        });
      }
    }
  }

  seleccionarProducto(producto: Producto): void {
    this.productoSeleccionado = producto;
    this.mostrarDetalles = true;
  }

  volverABusqueda(): void {
    this.mostrarDetalles = false;
    this.productoSeleccionado = null;
  }

  cerrar(): void {
    this.dialogRef.close();
  }

  // Métodos helper para formatear datos
  getPrecioFormateado(precio: string | number): string {
    const precioNum = typeof precio === 'string' ? parseFloat(precio) : precio;
    return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(precioNum || 0);
  }

  getEtiquetas(etiquetas: string[] | null | undefined): string {
    if (!etiquetas || etiquetas.length === 0) return 'N/A';
    return etiquetas.join(', ');
  }

  getIngredientes(ingredientes: string[] | null | undefined): string {
    if (!ingredientes || ingredientes.length === 0) return 'N/A';
    return ingredientes.join(', ');
  }

  tieneContenido(valor: string | null | undefined): boolean {
    return valor !== null && valor !== undefined && valor.trim() !== '';
  }
}

